import React, { useState } from 'react';
import { FixtureData } from '@graphql/generated/graphql';
import Select, { SelectOption } from '@amzn/meridian/select';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { escapeRegExp } from 'lodash';
import closeMediumTokens from '@amzn/meridian-tokens/base/icon/close-medium';
import Icon from '@amzn/meridian/icon';
import Button from '@amzn/meridian/button';
import Tooltip from '@amzn/meridian/tooltip';
import Input from '@amzn/meridian/input';
import { SubcategoryAllocation, ZoneCreationProperties } from '../types/zoneMapType';
import { ProductTaxonomy } from '../types/ProductTaxonomyTypes';
import { subcategoryColorByIndex, stringToColor, colorByKey } from '../zoneViewer/zoneMapColorHelper';
import { validateAllocations, sanitizePercentage } from '../allocation/validateAllocations';
import { reorderArray } from '../allocation/reorderArray';
import { useProgramConfig } from 'src/hooks/useProgramConfig';

interface ZoneCreationComponentProps {
  zoneIdx: number;
  fixtures: FixtureData[];
  selectedFixtures: string[];
  updateSubcategories: (zoneIdx: number, subcategoryIds?: string[], subcategoryNames?: string[]) => void;
  updateAllocations: (zoneIdx: number, allocations: SubcategoryAllocation[]) => void;
  zoneCreationProps: ZoneCreationProperties;
  productTaxonomy: ProductTaxonomy;
  isActive?: boolean;
  onActivate?: () => void;
  selectedDepartment?: string;
  /** GFC: categories available to this card's picker (already filtered to exclude
   * categories used by other cards). Empty/undefined for WFM/Fresh. */
  categoryOptions?: { categoryId: string; categoryName: string }[];
  /** GFC: invoked when the user picks a category for this card. */
  onCategoryChange?: (categoryId: string) => void;
  /** GFC: invoked when the user removes this card. */
  onRemove?: () => void;
  /** GFC: whether the remove (X) button is shown. */
  canRemove?: boolean;
}

const ZoneCreationComponent = (props: ZoneCreationComponentProps) => {
  const {
    zoneIdx,
    updateSubcategories,
    updateAllocations,
    zoneCreationProps,
    productTaxonomy,
    isActive = false,
    onActivate,
    categoryOptions,
    onCategoryChange,
    onRemove,
    canRemove = false,
  } = props;
  const selectedDepartment = props.selectedDepartment ?? '';
  const config = useProgramConfig();

  // GFC mode flags driven by program config (one zone == one Drop Zone == one categoryId).
  const isGfcDropZone = config.macroplanogramScope === 'category';
  const showDepartmentSelector = config.zoneMapBehavior.supportsDepartmentFilter;

  const zoneName = zoneCreationProps.zoneName || `Zone ${zoneIdx + 1}`;
  // GFC cards color by categoryId so the card border/tint matches the fixtures,
  // legend, and view-mode for the same Drop Zone. WFM/Fresh keep the name hash.
  const zoneColor =
    isGfcDropZone && zoneCreationProps.categoryId ? colorByKey(zoneCreationProps.categoryId) : stringToColor(zoneName);
  const allocations = zoneCreationProps.allocations ?? [];
  const validation = validateAllocations(allocations);

  // Drag-and-drop reorder state (local UI only, not propagated to ZoneViewer)
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  // GFC: search filter for the per-card Drop Zone picker.
  const [categorySearch, setCategorySearch] = useState('');
  const categorySearchRegExp = new RegExp(escapeRegExp(categorySearch), 'i');
  const searchableCategoryOptions = (categoryOptions ?? []).filter(
    (option) => !categorySearch || categorySearchRegExp.test(option.categoryName),
  );

  // Show subcategories from the selected category + any already-allocated subcategories
  // (even from other categories) to support cross-category merchandising
  const allocatedSubcategoryIds = new Set(allocations.map((a) => a.subcategoryId).filter(Boolean));
  const filteredSubcategoryOptions = productTaxonomy?.subcategoryList?.filter(
    (option) =>
      allocatedSubcategoryIds.has(option.subcategoryId) ||
      !zoneCreationProps.categoryId ||
      option.categoryId === zoneCreationProps.categoryId,
  );

  const handleAddAllocation = () => {
    const newAllocations: SubcategoryAllocation[] = [
      ...allocations,
      { subcategoryId: '', subcategoryName: '', percentage: 0, color: '#CCCCCC' },
    ];
    updateAllocations(zoneIdx, newAllocations);
  };

  const handleRemoveAllocation = (index: number) => {
    const remaining = allocations.filter((_, i) => i !== index);
    updateAllocations(zoneIdx, remaining);
    const subcategoryIds = remaining.map((a) => a.subcategoryId).filter(Boolean);
    const subcategoryNames = remaining.map((a) => a.subcategoryName).filter(Boolean);
    updateSubcategories(zoneIdx, subcategoryIds, subcategoryNames);
  };

  const handleSubcategoryChange = (index: number, subcategoryId: string) => {
    const subcategory = filteredSubcategoryOptions?.find((s) => s.subcategoryId === subcategoryId);
    if (!subcategory) return;

    const newAllocations = [...allocations];
    // Only assign a new color if this slot didn't already have one (i.e. it's a fresh selection)
    const existingColor = newAllocations[index]?.color;
    const isPlaceholder = !existingColor || existingColor === '#CCCCCC' || existingColor === '#cccccc';
    newAllocations[index] = {
      ...newAllocations[index],
      subcategoryId: subcategory.subcategoryId,
      subcategoryName: subcategory.subcategoryName,
      color: isPlaceholder ? subcategoryColorByIndex(index) : existingColor,
    };

    updateAllocations(zoneIdx, newAllocations);
    const subcategoryIds = newAllocations.map((a) => a.subcategoryId).filter(Boolean);
    const subcategoryNames = newAllocations.map((a) => a.subcategoryName).filter(Boolean);
    updateSubcategories(zoneIdx, subcategoryIds, subcategoryNames);
  };

  const showDragHandle = allocations.length >= 2;

  const handleDragStart = (index: number, e: React.DragEvent) => {
    setDragIndex(index);
    const { dataTransfer } = e;
    dataTransfer.setData('text/plain', String(index));
    dataTransfer.effectAllowed = 'move';
  };

  const handleMoveUp = (index: number) => {
    if (index > 0) {
      updateAllocations(zoneIdx, reorderArray(allocations, index, index - 1));
    }
  };

  const handleMoveDown = (index: number) => {
    if (index < allocations.length - 1) {
      updateAllocations(zoneIdx, reorderArray(allocations, index, index + 1));
    }
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDropTargetIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const { dataTransfer } = e;
    dataTransfer.dropEffect = 'move';
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const isAboveMidpoint = e.clientY < midpoint;
    // When cursor is in the top half, target is this row's index; bottom half targets the next index
    const targetIndex = isAboveMidpoint ? index : Math.min(index + 1, allocations.length - 1);
    setDropTargetIndex(targetIndex);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (isNaN(fromIndex) || dropTargetIndex === null) {
      handleDragEnd();
      return;
    }
    if (fromIndex !== dropTargetIndex) {
      updateAllocations(zoneIdx, reorderArray(allocations, fromIndex, dropTargetIndex));
    }
    handleDragEnd();
  };

  const getDropPosition = (index: number): 'above' | 'below' | null => {
    if (dropTargetIndex === null || dragIndex === null) return null;
    // Show indicator above the target row
    if (index === dropTargetIndex && dropTargetIndex !== dragIndex) {
      return 'above';
    }
    // When target is index+1 (bottom half hover), show indicator below the current row
    if (index === dropTargetIndex - 1 && dropTargetIndex !== dragIndex) {
      return 'below';
    }
    return null;
  };

  const handlePercentageChange = (index: number, value: string) => {
    if (value === '' || value === undefined) {
      const newAllocations = [...allocations];
      newAllocations[index] = { ...newAllocations[index], percentage: 0 };
      updateAllocations(zoneIdx, newAllocations);
      return;
    }
    const parsed = parseFloat(value);
    if (isNaN(parsed)) return;
    const sanitized = sanitizePercentage(parsed);
    if (sanitized === null) return; // reject out-of-range

    const newAllocations = [...allocations];
    newAllocations[index] = { ...newAllocations[index], percentage: sanitized };
    updateAllocations(zoneIdx, newAllocations);
  };

  return (
    <div
      onClick={() => onActivate && onActivate()}
      style={{
        cursor: onActivate ? 'pointer' : 'default',
        padding: '12px',
        border: isActive ? `3px solid ${zoneColor}` : '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: isActive ? `${zoneColor}15` : 'transparent',
        transition: 'all 0.2s ease',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '420px',
      }}
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : undefined}
      aria-label={onActivate ? `Set ${zoneName} as active zone` : undefined}
    >
      <Column spacing="300">
        {/* Allocation rows */}
        {showDepartmentSelector && !selectedDepartment ? (
          <Text type="b200" color="secondary">
            Select a department to configure subcategory allocations.
          </Text>
        ) : isGfcDropZone ? (
          /* GFC Drop Zone mode: per-card category picker, fixture count, active hint. */
          <>
            <Row alignmentVertical="center" alignmentHorizontal="justify" spacing="200">
              <Text type="b300">
                <strong>Drop Zone {zoneIdx + 1}</strong>
              </Text>
              {canRemove && onRemove && (
                <div onClick={(e) => e.stopPropagation()}>
                  <Button onClick={onRemove} type="icon" size="small">
                    <Icon tokens={closeMediumTokens}>Remove</Icon>
                  </Button>
                </div>
              )}
            </Row>
            <Select
              label={config.labels.category}
              value={zoneCreationProps.categoryId || undefined}
              onChange={(categoryId: string) => onCategoryChange?.(categoryId)}
              searchQuery={categorySearch}
              onSearch={setCategorySearch}
              placeholder={`Choose a ${config.labels.category.toLowerCase()}...`}
              width="100%"
            >
              {searchableCategoryOptions.map((option) => (
                <SelectOption label={option.categoryName} value={option.categoryId} key={option.categoryId} />
              ))}
              {searchableCategoryOptions.length === 0 ? (
                <Column alignmentVertical="center" spacing="300" spacingInset="600">
                  <Text alignment="center">No results</Text>
                </Column>
              ) : null}
            </Select>
            <Text type="b200" color="secondary">
              {zoneCreationProps.fixtureIds.length} fixtures assigned
            </Text>
            {!zoneCreationProps.categoryId ? (
              <Text type="b200" color="secondary">
                Pick a {config.labels.category.toLowerCase()} above, then click fixtures to assign them.
              </Text>
            ) : isActive ? (
              <Text type="b200" color="secondary">
                Active — click fixtures to add or remove from this Drop Zone.
              </Text>
            ) : (
              <Text type="b200" color="secondary">
                Click anywhere on this card to make it active.
              </Text>
            )}
          </>
        ) : allocations.length === 0 ? (
          <Text type="b200" color="secondary">
            Add a subcategory allocation to get started.
          </Text>
        ) : (
          allocations.map((allocation, index) => (
            <AllocationRow
              key={index}
              allocation={allocation}
              index={index}
              subcategoryOptions={filteredSubcategoryOptions}
              allocatedSubcategoryIds={allocatedSubcategoryIds}
              onSubcategoryChange={handleSubcategoryChange}
              onPercentageChange={handlePercentageChange}
              onRemove={handleRemoveAllocation}
              isDragging={dragIndex === index}
              isDropTarget={getDropPosition(index) !== null}
              dropPosition={getDropPosition(index)}
              showDragHandle={showDragHandle}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
            />
          ))
        )}

        {/* Percentage sum display — only for percentage mode */}
        {!isGfcDropZone && selectedDepartment && allocations.length > 0 && (
          <Row alignmentVertical="center" spacing="200">
            <Text type="b200">Total: {validation.sum}%</Text>
            {!validation.isValid && validation.sum !== 100 && (
              <Text type="b200" color="error">
                (must equal 100%)
              </Text>
            )}
          </Row>
        )}

        {!isGfcDropZone && selectedDepartment && validation.sum < 100 && (
          <Button onClick={handleAddAllocation} size="small">
            Add subcategory
          </Button>
        )}
      </Column>
    </div>
  );
};

interface AllocationRowProps {
  allocation: SubcategoryAllocation;
  index: number;
  subcategoryOptions: { subcategoryId: string; subcategoryName: string }[] | undefined;
  allocatedSubcategoryIds: Set<string>;
  onSubcategoryChange: (index: number, subcategoryId: string) => void;
  onPercentageChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  isDragging: boolean;
  isDropTarget: boolean;
  dropPosition: 'above' | 'below' | null;
  showDragHandle: boolean;
  onDragStart: (index: number, e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
}

const AllocationRow = ({
  allocation,
  index,
  subcategoryOptions,
  allocatedSubcategoryIds,
  onSubcategoryChange,
  onPercentageChange,
  onRemove,
  isDragging,
  isDropTarget,
  dropPosition,
  showDragHandle,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onMoveUp,
  onMoveDown,
}: AllocationRowProps) => {
  const [searchFilter, setSearchFilter] = useState('');
  const searchRegExp = new RegExp(escapeRegExp(searchFilter), 'i');

  const availableOptions = subcategoryOptions?.filter(
    (option) =>
      (option.subcategoryId === allocation.subcategoryId || !allocatedSubcategoryIds.has(option.subcategoryId)) &&
      (!searchFilter || searchRegExp.test(option.subcategoryName)),
  );

  const dropIndicatorStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: '#0073bb',
    pointerEvents: 'none',
  };

  return (
    <div
      style={{ position: 'relative', opacity: isDragging ? 0.4 : 1, transition: 'opacity 0.2s ease' }}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={onDrop}
    >
      {/* Drop indicator line — above */}
      {isDropTarget && dropPosition === 'above' && (
        <div style={{ ...dropIndicatorStyle, top: 0 }} data-testid="drop-indicator-above" />
      )}
      <Row spacing="100" alignmentVertical="center" width="100%">
        {/* Drag handle — only shown when 2+ allocations */}
        {showDragHandle && (
          <span
            draggable="true"
            tabIndex={0}
            role="button"
            aria-roledescription="sortable"
            aria-label={`Reorder ${allocation.subcategoryName || 'allocation'}`}
            onDragStart={(e) => onDragStart(index, e)}
            onDragEnd={onDragEnd}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                onMoveUp(index);
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                onMoveDown(index);
              }
            }}
            style={{
              cursor: 'grab',
              userSelect: 'none',
              fontSize: '16px',
              lineHeight: 1,
              color: '#666',
              flexShrink: 0,
              padding: '0 2px',
            }}
          >
            ⠿
          </span>
        )}
        {/* Color swatch */}
        <div
          style={{
            width: '12px',
            height: '12px',
            backgroundColor: allocation.color,
            borderRadius: '2px',
            border: '1px solid #999',
            flexShrink: 0,
          }}
          aria-hidden="true"
        />
        {/* Subcategory selector */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Select
            value={allocation.subcategoryId || undefined}
            onChange={(val: string) => onSubcategoryChange(index, val)}
            searchQuery={searchFilter}
            onSearch={setSearchFilter}
            placeholder="Subcategory..."
            size="small"
            width="100%"
          >
            {availableOptions?.map((option) => (
              <SelectOption label={option.subcategoryName} value={option.subcategoryId} key={option.subcategoryId} />
            ))}
            {!availableOptions || availableOptions.length === 0 ? (
              <Column alignmentVertical="center" spacing="300" spacingInset="600">
                <Text alignment="center">No results</Text>
              </Column>
            ) : null}
          </Select>
        </div>
        {/* Percentage input */}
        <div style={{ width: '65px', flexShrink: 0 }}>
          <Input
            value={String(allocation.percentage)}
            onChange={(val: string) => onPercentageChange(index, val)}
            size="small"
          />
        </div>
        {/* Remove button */}
        <Tooltip position="end" title="Remove allocation">
          <Button onClick={() => onRemove(index)} size="small">
            <Icon tokens={closeMediumTokens} />
          </Button>
        </Tooltip>
      </Row>
      {/* Drop indicator line — below */}
      {isDropTarget && dropPosition === 'below' && (
        <div style={{ ...dropIndicatorStyle, bottom: 0 }} data-testid="drop-indicator-below" />
      )}
    </div>
  );
};

export default ZoneCreationComponent;
